import { Box, Flex } from '@chakra-ui/react';
import { Grid, gridConfig } from '@time-line/components/common';
import { useImage } from 'lib/sanity';
import Img from 'next/image';
import { useMemo } from 'react';
import type { ReviewsCarousel as ReviewsCarouselProps } from 'types/cms/blocks';

import { ReviewSummaryCard } from './ReviewSummaryCard';
import { RichReviewsMarquee } from './RichReviewsMarquee';
import { useReviewsCarouselStats } from './useReviewsCarouselStats';

const { borderWidthClamp, gridBorderRadius } = gridConfig;

export function ReviewsCarouselRich({
  showOverview,
  showRecommendationScore,
  backgroundImage,
  reviews,
}: ReviewsCarouselProps) {
  const { stats, isMultiProduct } = useReviewsCarouselStats(reviews);

  const backgroundProps = useImage(backgroundImage ?? null, { fill: true });

  const { isSingleProduct, formatThumbnail } = useMemo(() => {
    const slugs = new Set(
      reviews.map((review) => review.product?.slug).filter(Boolean),
    );
    const single = slugs.size <= 1;
    return {
      isSingleProduct: single,
      formatThumbnail: single
        ? reviews.find((review) => review.product?.formatThumbnail)?.product
            ?.formatThumbnail
        : undefined,
    };
  }, [reviews]);

  return (
    <Grid
      className="section--reviews-overview-carousel-rich"
      my={borderWidthClamp}
    >
      <Grid.Inner gridColumn="bordered">
        <Box gridColumn="span 12">
          <Box
            position="relative"
            overflow="hidden"
            borderRadius={gridBorderRadius}
            background={backgroundImage?.palette?.dominantBg || 'gray.600'}
            color="gray.50"
            py={{ base: 0, md: 'lg' }}
            pl={{ base: 0, md: 'lg' }}
          >
            {/* Full-bleed background image */}
            <Box position="absolute" inset={0} zIndex={0}>
              {backgroundImage && backgroundProps && (
                <Img
                  {...backgroundProps}
                  role="presentation"
                  alt=""
                  sizes="100vw"
                  style={{ objectFit: 'cover' }}
                />
              )}
            </Box>

            <Flex
              position="relative"
              zIndex={1}
              direction={{ base: 'column', md: 'row' }}
              align="center"
              gap={{ base: 'lg', md: 'sm' }}
            >
              <Box
                order={{ base: 2, md: 1 }}
                w={{ base: 'full', md: 'auto' }}
                flexShrink={0}
              >
                <ReviewSummaryCard
                  overview={stats}
                  isMultiProduct={isMultiProduct}
                  showOverview={showOverview}
                  showRecommendationScore={showRecommendationScore}
                  formatThumbnail={
                    isSingleProduct ? formatThumbnail : undefined
                  }
                />
              </Box>

              <Box
                order={{ base: 1, md: 2 }}
                flex="1"
                minW={0}
                w="full"
                overflow="hidden"
                pt={{ base: 'xl', md: 0 }}
              >
                <RichReviewsMarquee
                  reviews={reviews}
                  showPackagingThumbnail={!isSingleProduct}
                  clipGradientWidth={30}
                  clipGradientWidthRight={0}
                  autoFill
                  sx={{
                    '.rfm-marquee, .rfm-initial-child-container': {
                      alignItems: 'flex-start',
                    },
                  }}
                />
              </Box>
            </Flex>
          </Box>
        </Box>
      </Grid.Inner>
    </Grid>
  );
}
